import styled from '@emotion/styled';
import React from 'react';

import type { LensButtonProps } from '../../types';
import { getColorValue, getFocusRing, u } from '../../utilities';

import Icon from '../icon/icon';

const sizeStyles = {
  small: {
    size: u(3),
    iconSize: 2.25,
    radius: 'var(--ds-radius-small)',
  },
  medium: {
    size: u(4),
    iconSize: 3,
    radius: 'var(--ds-radius-medium)',
  },
  large: {
    size: u(5),
    iconSize: 4,
    radius: 'var(--ds-radius-large)',
  },
};

export const IconButtonBox = styled.button<IconButtonBoxProps>`
  background-color: ${props =>
    getColorValue(
      props.isActive ? 'backgroundActive' : props.backgroundColor
    ) || 'transparent'};
  border: none;
  appearance: none;
  cursor: pointer;
  padding: 0;
  width: ${props => sizeStyles[props.size].size};
  height: ${props => sizeStyles[props.size].size};
  position: relative;
  outline: 1px solid transparent;
  transition: 0.6s background-color;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  vertical-align: middle;
  border-radius: ${props => sizeStyles[props.size].radius};
  font: inherit;

  &:hover {
    transition: 0.3s background-color;
    background-color: ${props =>
      getColorValue(props.isActive ? 'backgroundActive' : 'backgroundHover')};
  }

  &:active {
    transition: 0s background-color;
    background-color: ${getColorValue('backgroundActive')};
  }

  &:disabled {
    color: ${getColorValue('disabledContent')};
    pointer-events: none;
  }

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    display: block;
    position: absolute;
    top: 0;
    border-radius: ${props => sizeStyles[props.size].radius};
  }

  &:focus-visible:before {
    ${getFocusRing()};
  }

  &:focus::-moz-focus-inner {
    border: 0;
  }
`;

type AllProps = IconButtonProps &
  React.ComponentProps<typeof IconButtonBox> &
  Omit<
    LensButtonProps,
    keyof IconButtonProps & React.ComponentProps<typeof IconButtonBox>
  >;

const IconButton = React.forwardRef(
  (
    {
      altText,
      icon,
      onClick,
      iconColor = 'body',
      backgroundColor,
      isActive,
      isDisabled,
      size = 'medium',
      htmlTag = 'button',
      ...props
    }: AllProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => (
    <IconButtonBox
      aria-label={altText}
      onClick={htmlTag === 'button' ? onClick : undefined}
      isActive={isActive}
      disabled={isDisabled}
      size={size}
      backgroundColor={backgroundColor}
      as={htmlTag}
      ref={ref}
      {...props}
    >
      <Icon
        icon={icon}
        size={sizeStyles[size].iconSize}
        color={isDisabled ? 'disabledContent' : iconColor}
      />
    </IconButtonBox>
  )
);

IconButton.displayName = 'IconButton';

type IconButtonProps = {
  altText: string;
  icon?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  iconColor?: string;
  backgroundColor?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  htmlTag?: 'button' | 'a';
  href?: string;
  target?: string;
};

type IconButtonBoxProps = {
  size?: 'small' | 'medium' | 'large';
  isActive?: boolean;
  backgroundColor?: string;
  as?: 'button' | 'a';
};

export default IconButton;
